import { CustomButton } from '@/shared/ui/components';
import _ from 'lodash';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import eyeIcon from '../assets/eye-icon.svg';
import eyeSlashIcon from '../assets/eye-slash-icon.svg';
import './toggleShChannel.scss';
import type { ToggleShChannelProps } from './toggleShChannel.types';

/**
 * Show and hide toggles for ECG channels CH1 through CH3, each button reflecting current visibility with an eye or crossed-eye icon and tooltip. Derives per-channel presence from visibleChannels, blocks a toggle through disableCondition when it would hide the only visible channel, and reports the pressed channel index through onChange.
 *
 * @param visibleChannels - Indexes of the channels currently visible.
 * @param onChange - Callback receiving the toggled channel index.
 * @param disabled - Whether all toggles are disabled.
 * @returns The rendered channel toggle group.
 */
const ToggleShChannel = ({ visibleChannels, onChange, disabled }: ToggleShChannelProps) => {
  const isSingleChannel = visibleChannels.length <= 1;
  const foundCH1 = _.includes(visibleChannels, 0);
  const foundCH2 = _.includes(visibleChannels, 1);
  const foundCH3 = _.includes(visibleChannels, 2);

  /**
   * Decides whether a channel visibility toggle must stay disabled. Returns true when only one channel is visible and it is the requested channel, so the last visible channel cannot be hidden, otherwise falls back to the disabled prop.
   *
   * @param channelIdx - Index of the channel whose toggle is being evaluated.
   * @returns True when the toggle must be disabled, otherwise the disabled prop.
   */
  const disableCondition = (channelIdx: any) => {
    if (isSingleChannel && channelIdx === visibleChannels[0]) {
      return true;
    }
    return disabled;
  };

  return (
    <div className="toggle-sh-channel">
      <CustomButton
        size="small"
        className="toggle-sh-channel__button"
        type="outline"
        style="neutral"
        tooltipTitle={
          disableCondition(0) ? undefined : foundCH1 ? t`Hide channel 1` : t`Show channel 1`
        }
        icon={<ReactSVG src={foundCH1 ? eyeIcon : eyeSlashIcon} />}
        disabled={disableCondition(0)}
        onClick={() => {
          onChange(0);
        }}
      >
        {t`CH1`}
      </CustomButton>
      <CustomButton
        size="small"
        className="toggle-sh-channel__button"
        type="outline"
        style="neutral"
        tooltipTitle={
          disableCondition(1) ? undefined : foundCH2 ? t`Hide channel 2` : t`Show channel 2`
        }
        icon={<ReactSVG src={foundCH2 ? eyeIcon : eyeSlashIcon} />}
        disabled={disableCondition(1)}
        onClick={() => {
          onChange(1);
        }}
      >
        {t`CH2`}
      </CustomButton>
      <CustomButton
        size="small"
        className="toggle-sh-channel__button"
        type="outline"
        style="neutral"
        tooltipTitle={
          disableCondition(2) ? undefined : foundCH3 ? t`Hide channel 3` : t`Show channel 3`
        }
        icon={<ReactSVG src={foundCH3 ? eyeIcon : eyeSlashIcon} />}
        disabled={disableCondition(2)}
        onClick={() => {
          onChange(2);
        }}
      >
        {t`CH3`}
      </CustomButton>
    </div>
  );
};

export default ToggleShChannel;
